import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import { CheckCircle2, Circle, ArrowRight, Zap, Building2, Image as ImageIcon, Package, Users2, Sparkles, Plus, Trash2, Mail } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  { key: "business", label: "Business info", icon: Building2 },
  { key: "logo", label: "Logo & photos", icon: ImageIcon },
  { key: "services", label: "Services & pricing", icon: Package },
  { key: "team", label: "Team members", icon: Users2 },
  { key: "review", label: "Review & launch", icon: Sparkles },
];

export default function Onboarding() {
  const { business, refresh, user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState(null);

  const [biz, setBiz] = useState({
    business_name: "",
    service_type: "",
    phone: "",
    contact_email: "",
    target_location: "",
    service_area: "",
  });
  const [savingBiz, setSavingBiz] = useState(false);

  const [services, setServices] = useState([]);
  const [svcForm, setSvcForm] = useState({ name: "", description: "", default_price: "", taxable: true });
  const [svcBusy, setSvcBusy] = useState(false);

  const [inviteForm, setInviteForm] = useState({ email: "", role: "technician", full_name: "" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [invites, setInvites] = useState([]);

  const loadStatus = async () => {
    try {
      const [s, b, svc, inv] = await Promise.all([
        api.get("/onboarding/status"),
        api.get("/business"),
        api.get("/services"),
        api.get("/team/invites"),
      ]);
      setStatus(s.data);
      setBiz({
        business_name: b.data.business_name || "",
        service_type: b.data.service_type || "",
        phone: b.data.phone || "",
        contact_email: b.data.contact_email || "",
        target_location: b.data.target_location || "",
        service_area: b.data.service_area || "",
      });
      setServices(svc.data);
      setInvites(inv.data);
      setStep(Number(s.data.step) || 0);
    } catch { toast.error("Failed to load onboarding state"); }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  const persistStep = async (n) => {
    setStep(n);
    try { await api.post("/onboarding/step", { step: n }); } catch { /* silent */ }
  };

  const saveBiz = async () => {
    if (!biz.business_name?.trim()) { toast.error("Business name is required"); return false; }
    setSavingBiz(true);
    try {
      await api.put("/business", biz);
      await refresh();
      toast.success("Business info saved");
      return true;
    } catch { toast.error("Save failed"); return false; }
    finally { setSavingBiz(false); }
  };

  const addService = async (e) => {
    e?.preventDefault?.();
    if (!svcForm.name?.trim()) { toast.error("Service name required"); return; }
    setSvcBusy(true);
    try {
      await api.post("/services", { ...svcForm, default_price: parseFloat(svcForm.default_price || 0) });
      toast.success("Service added");
      setSvcForm({ name: "", description: "", default_price: "", taxable: true });
      const { data } = await api.get("/services");
      setServices(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSvcBusy(false); }
  };

  const removeService = async (id) => {
    if (!window.confirm("Remove this service?")) return;
    try {
      await api.delete(`/services/${id}`);
      setServices(services.filter((s) => s.id !== id));
    } catch { toast.error("Failed"); }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) { toast.error("Email required"); return; }
    setInviteBusy(true);
    try {
      const { data } = await api.post("/team/invites", inviteForm);
      toast.success(`Invite sent to ${inviteForm.email}`);
      setInviteForm({ email: "", role: "technician", full_name: "" });
      setInvites([data, ...invites]);
      if (data?.accept_url) {
        try { await navigator.clipboard.writeText(data.accept_url); toast.info("Link also copied"); } catch {}
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setInviteBusy(false); }
  };

  const finish = async () => {
    if (!await saveBiz()) return;
    try {
      await api.post("/onboarding/complete");
      await refresh();
      toast.success("Welcome to LeadForge!");
      nav("/app");
    } catch { toast.error("Failed to complete onboarding"); }
  };

  if (user?.is_super_admin || business?.onboarding_completed) {
    // Onboarded users shouldn't be here; but allow re-entry if URL directly accessed.
  }

  const goNext = async () => {
    if (step === 0) { if (!await saveBiz()) return; }
    persistStep(Math.min(step + 1, STEPS.length - 1));
  };
  const goBack = () => persistStep(Math.max(step - 1, 0));

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top bar */}
      <div className="bg-zinc-950 text-white px-6 py-4 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#FF5A00] flex items-center justify-center"><Zap className="w-5 h-5 text-white" strokeWidth={2.5}/></div>
          <div className="font-heading font-bold text-lg">LeadForge · Setup</div>
          <div className="ml-auto text-xs text-zinc-400 hidden sm:block">
            Welcome{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""} · {biz.business_name || "your business"}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 md:p-8">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="onboarding-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.key} className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={()=>persistStep(i)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition ${
                    active ? "border-[#FF5A00] bg-white text-[#FF5A00] font-medium" :
                    done ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                    "border-zinc-200 bg-white text-zinc-500 hover:border-zinc-300"
                  }`}
                  data-testid={`onboarding-step-${s.key}`}
                >
                  {done ? <CheckCircle2 className="w-4 h-4"/> : <Icon className="w-4 h-4"/>}
                  <span>{i + 1}. {s.label}</span>
                </button>
                {i < STEPS.length - 1 && <ArrowRight className="w-3 h-3 text-zinc-300"/>}
              </div>
            );
          })}
        </div>

        {/* Step body */}
        <div className="bg-white border border-zinc-200 rounded-xl p-6 md:p-8 card-crisp">
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-panel-business">
              <div>
                <h1 className="font-heading text-2xl font-bold">Tell us about your business</h1>
                <p className="text-zinc-500 text-sm mt-1">Your customers will see this info on estimates & invoices.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Business name *</Label>
                  <Input value={biz.business_name} onChange={(e)=>setBiz({...biz, business_name: e.target.value})} data-testid="onboarding-biz-name"/>
                </div>
                <div>
                  <Label>Service type</Label>
                  <Input placeholder="Plumbing, HVAC, Landscaping…" value={biz.service_type} onChange={(e)=>setBiz({...biz, service_type: e.target.value})} data-testid="onboarding-biz-service-type"/>
                </div>
                <div>
                  <Label>Business phone</Label>
                  <Input value={biz.phone} onChange={(e)=>setBiz({...biz, phone: e.target.value})} data-testid="onboarding-biz-phone"/>
                </div>
                <div>
                  <Label>Contact email</Label>
                  <Input type="email" value={biz.contact_email} onChange={(e)=>setBiz({...biz, contact_email: e.target.value})} data-testid="onboarding-biz-email"/>
                </div>
                <div>
                  <Label>City/State</Label>
                  <Input placeholder="Chicago, IL" value={biz.target_location} onChange={(e)=>setBiz({...biz, target_location: e.target.value})} data-testid="onboarding-biz-location"/>
                </div>
                <div className="md:col-span-2">
                  <Label>Service area</Label>
                  <Input placeholder="30-mile radius from HQ" value={biz.service_area} onChange={(e)=>setBiz({...biz, service_area: e.target.value})} data-testid="onboarding-biz-area"/>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-panel-logo">
              <div>
                <h1 className="font-heading text-2xl font-bold">Add your logo</h1>
                <p className="text-zinc-500 text-sm mt-1">Upload a square logo (PNG/JPG). You can skip this and do it later.</p>
              </div>
              {business?.id && (
                <FileUploader resourceType="business" resourceId={business.id} title="Business logo & photos"/>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="onboarding-panel-services">
              <div>
                <h1 className="font-heading text-2xl font-bold">List your services</h1>
                <p className="text-zinc-500 text-sm mt-1">Add at least one so you can quickly build estimates. You can add more later.</p>
              </div>
              <form onSubmit={addService} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div className="md:col-span-2">
                  <Label>Service name</Label>
                  <Input required value={svcForm.name} onChange={(e)=>setSvcForm({...svcForm, name: e.target.value})} placeholder="e.g. AC Tune-Up" data-testid="onboarding-svc-name"/>
                </div>
                <div>
                  <Label>Default price ($)</Label>
                  <Input type="number" step="0.01" value={svcForm.default_price} onChange={(e)=>setSvcForm({...svcForm, default_price: e.target.value})} data-testid="onboarding-svc-price"/>
                </div>
                <Button type="submit" disabled={svcBusy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="onboarding-svc-add"><Plus className="w-4 h-4 mr-1"/>Add</Button>
                <div className="md:col-span-4">
                  <Label>Description (optional)</Label>
                  <Input value={svcForm.description} onChange={(e)=>setSvcForm({...svcForm, description: e.target.value})} data-testid="onboarding-svc-desc"/>
                </div>
              </form>
              <div className="border-t border-zinc-100 pt-4">
                <div className="text-xs uppercase font-mono-accent text-zinc-500 tracking-widest mb-2">Your services ({services.length})</div>
                {services.length === 0 ? (
                  <div className="text-sm text-zinc-400 py-6 text-center">No services yet. Add your first above.</div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {services.map((s) => (
                      <div key={s.id} className="py-2 flex items-center justify-between" data-testid={`onboarding-svc-row-${s.id}`}>
                        <div>
                          <div className="font-medium">{s.name}</div>
                          {s.description && <div className="text-xs text-zinc-500">{s.description}</div>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-mono-accent">${Number(s.default_price || 0).toFixed(2)}</span>
                          <button onClick={()=>removeService(s.id)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded"><Trash2 className="w-4 h-4"/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="onboarding-panel-team">
              <div>
                <h1 className="font-heading text-2xl font-bold">Invite your team</h1>
                <p className="text-zinc-500 text-sm mt-1">Optional. You can add teammates now or later. Roles: admin, sales, technician.</p>
              </div>
              <form onSubmit={sendInvite} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div className="md:col-span-2">
                  <Label>Teammate email</Label>
                  <Input type="email" value={inviteForm.email} onChange={(e)=>setInviteForm({...inviteForm, email: e.target.value})} data-testid="onboarding-invite-email"/>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={inviteForm.role} onValueChange={(v)=>setInviteForm({...inviteForm, role: v})}>
                    <SelectTrigger data-testid="onboarding-invite-role"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="technician">Technician</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={inviteBusy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="onboarding-invite-send"><Mail className="w-4 h-4 mr-1"/>Send</Button>
              </form>
              <div className="border-t border-zinc-100 pt-4">
                <div className="text-xs uppercase font-mono-accent text-zinc-500 tracking-widest mb-2">Invites sent</div>
                {invites.length === 0 ? (
                  <div className="text-sm text-zinc-400 py-4 text-center">None yet. That's fine — you can invite anytime from Settings.</div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {invites.map((i) => (
                      <div key={i.id} className="py-2 flex items-center justify-between text-sm">
                        <div>{i.email} · <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-100 font-mono-accent uppercase">{i.role}</span></div>
                        <span className="text-xs text-zinc-500">{i.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="onboarding-panel-review">
              <div>
                <h1 className="font-heading text-2xl font-bold">You're ready to launch</h1>
                <p className="text-zinc-500 text-sm mt-1">Review your setup and start capturing leads.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <ChecklistItem label="Business info" done={!!biz.business_name}/>
                <ChecklistItem label="Contact info" done={!!(biz.phone && biz.contact_email)}/>
                <ChecklistItem label="Service area" done={!!(biz.target_location || biz.service_area)}/>
                <ChecklistItem label="Services listed" done={services.length > 0}/>
                <ChecklistItem label="Logo uploaded" done={(status?.counts?.logo || 0) > 0} optional/>
                <ChecklistItem label="Team invited" done={invites.length > 0} optional/>
              </div>
              <div className="pt-4">
                <Button onClick={finish} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift px-8" data-testid="onboarding-finish-btn">
                  Complete setup & go to dashboard
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom nav */}
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={goBack} disabled={step === 0} data-testid="onboarding-back-btn">Back</Button>
          <div className="text-xs text-zinc-500 font-mono-accent">Step {step + 1} of {STEPS.length}</div>
          {step < STEPS.length - 1 ? (
            <Button onClick={goNext} disabled={savingBiz} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="onboarding-next-btn">
              {savingBiz ? "Saving…" : "Continue"}
            </Button>
          ) : (
            <Button onClick={finish} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="onboarding-finish-nav-btn">
              Finish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChecklistItem({ label, done, optional }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${done ? "border-emerald-200 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}>
      {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <Circle className="w-4 h-4 text-zinc-300"/>}
      <span>{label}</span>
      {optional && <span className="text-[10px] uppercase tracking-widest font-mono-accent text-zinc-400 ml-auto">optional</span>}
    </div>
  );
}
